import { useRef, useCallback, useEffect } from 'react';
import { type OperationVariables } from '@apollo/react-common';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useLazyQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';

import {
	ApolloErrorInterop,
	isApolloError,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { executeRelayQuery } from './executeRelayQuery.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { mapFetchPolicyForApollo } from './mapFetchPolicyForApollo.ts';
import { getRelayOperationName } from './relayOperation.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { useMemoObj } from './useMemoObj.ts';
import type { UseQueryInteropSupportedHookOptions } from './useQueryInterop.ts';

export type useLazyQueryPromiseInteropOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = UseQueryInteropSupportedHookOptions<TData, TVariables>;

type DoQuery<TData, TVariables extends Record<string, any>> = (
	options?: useLazyQueryPromiseInteropOptions<TData, TVariables>,
) => Promise<TData>;

/**
 * Coerce an unknown thrown value into an ApolloErrorInterop, so a rejection carries Apollo's
 * `graphQLErrors` / `networkError` fields on both branches. Mirrors the helper in
 * `useLazyQueryInterop`.
 */
function toApolloError(err: unknown): ApolloErrorInterop {
	if (isApolloError(err as Error)) return err as ApolloErrorInterop;
	const networkError = err instanceof Error ? err : new Error(String(err));
	return new ApolloErrorInterop({ networkError });
}

export function useLazyQueryPromiseInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: useLazyQueryPromiseInteropOptions<TData, TVariables>,
): DoQuery<TData, TVariables> {
	const operationName = getRelayOperationName(query);
	if (isRelayEnabled(options?.__callerPackage__, { operationName, phase: 'lazy' })) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
		return useLazyQueryPromiseInteropRelay<TData, TVariables>(query, options);
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
	return useLazyQueryPromiseInteropApollo<TData, TVariables>(query, options);
}

function useLazyQueryPromiseInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: useLazyQueryPromiseInteropOptions<TData, TVariables>,
): DoQuery<TData, TVariables> {
	const resolversRef = useRef<{
		resolve(data: TData): void;
		reject(error: ApolloErrorInterop): void;
	}>();
	/**
	 * NOTE: Using `onCompleted` and `onError` callbacks does not work as expected due to Apollo bug which is present in both v2 and v3 - https://github.com/apollographql/apollo-client/issues/9338
	 */
	// Interop-only fetch policies must be translated or they reach Apollo untranslated and silently
	// degrade to its default cache behaviour. That matters for `'no-store'` specifically: Apollo's
	// `'no-cache'` is its exact equivalent, and without this mapping a query that is correct today
	// would start writing to the cache.
	const [doQuery, { data, error }] = useLazyQuery<TData, TVariables>(
		...[
			toDocumentNode(query),
			options && {
				...options,
				fetchPolicy: mapFetchPolicyForApollo(options.fetchPolicy),
				...(options.nextFetchPolicy
					? { nextFetchPolicy: mapFetchPolicyForApollo(options.nextFetchPolicy) }
					: {}),
			},
		],
	);

	useEffect(() => {
		if (data) resolversRef.current?.resolve?.(data);
		if (error) resolversRef.current?.reject?.(error);
	}, [data, error]);

	return useCallback(
		(vars?: useLazyQueryPromiseInteropOptions<TData, TVariables>) => {
			let resolve: (data: TData) => void;
			let reject: (error: ApolloErrorInterop) => void;
			const promise = new Promise<TData>((res, rej) => {
				resolve = res;
				reject = rej;
			});

			// @ts-expect-error `resolve` and `reject` *will* be assigned via the Promise constructor, but TS can't guarantee that. This should be replaced with `Promise.withResolvers` once out jest setup properly supports that
			resolversRef.current = { resolve, reject };

			doQuery(vars);

			return promise;
		},
		[doQuery],
	);
}

function useLazyQueryPromiseInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: useLazyQueryPromiseInteropOptions<TData, TVariables>,
): DoQuery<TData, TVariables> {
	// Validate before any hook call so the throw isn't masked by useRelayEnvironment().
	if (!('params' in query)) {
		throw new Error(
			`[Relay migration] unsupported tagged node provided for operation "${getRelayOperationName(query) ?? 'unknown'}". Please make sure you generated Relay artifacts for your operation`,
		);
	}

	const environment = useRelayEnvironment();
	const operationName = getRelayOperationName(query) ?? undefined;
	const hookVariables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__, {
		operationName,
		phase: 'memo',
	});

	// Read through refs rather than closing over these directly, so the returned callback keeps a
	// `[environment, query]` dependency list. Every current caller drives it from a `useEffect`
	// dependency array, so a new identity per render would re-fire the query on each render.
	// `context` in particular is usually an inline object and would change identity every time.
	const latestOptionsRef = useRef(options);
	latestOptionsRef.current = options;
	const hookVariablesRef = useRef(hookVariables);
	hookVariablesRef.current = hookVariables;

	return useCallback(
		(callOptions?: useLazyQueryPromiseInteropOptions<TData, TVariables>) => {
			const hookOptions = latestOptionsRef.current;

			// Per-call options win over hook-level ones, and variables merge rather than replace —
			// matching Apollo, which merges the variables handed to its lazy `doQuery` over the ones
			// given to the hook.
			const variables = {
				...hookVariablesRef.current,
				...callOptions?.variables,
			} as TVariables;
			// Shared with `queryInterop` so the fetch-policy mapping and the `noStore` derivation have
			// one home. `noStore` is load-bearing for this hook specifically: `PagePanelContentQuery`
			// runs through it and is one of the operations whose response carries two `Content` objects
			// sharing an id, so a missed flag reintroduces the record collision.
			return executeRelayQuery<TData, TVariables>({
				environment,
				query,
				variables,
				fetchPolicy: callOptions?.fetchPolicy ?? hookOptions?.fetchPolicy,
				errorPolicy: callOptions?.errorPolicy ?? hookOptions?.errorPolicy ?? 'none',
				context: callOptions?.context ?? hookOptions?.context,
			})
				.then((result) => {
					// Diverges from `queryInterop`, which hands `{ data, error }` back: this hook mirrors
					// Apollo's promise, which rejects. Under `errorPolicy: 'all'` a result carries both
					// data and error, and Apollo's effect resolves before it rejects — a promise settles
					// once — so data being present means resolve.
					if (result.error && result.data === undefined) {
						throw result.error;
					}
					return result.data;
				})
				.catch((err) => {
					// Reject with an ApolloErrorInterop on both branches, so callers can read
					// `graphQLErrors` / `networkError` without knowing which branch ran.
					throw toApolloError(err);
				});
		},
		[environment, query],
	);
}
